import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import LanguageAutocomplete from "@/components/LanguageAutocomplete";
import ToggleSwitch from "@/components/ToggleSwitch";
import ResponsiveDialog from "@/features/ResponsiveDialog/ResponsiveDialog";
import DialogCloseBtn from "@/features/ResponsiveDialog/styled/DialogCloseBtn";
import CircularRectTextButton from "@/components/CircularButton/styled/CircularRectTextButton";
import Select from "@/components/Select/Select";

import br from "@/styles/breakpoints.module.css";
import Mixpanel from "@/utils/analytics/Mixpanel";
import { borderRadius } from "@/utils/borderRadius";
import { iconSizes } from "@/utils/iconProps";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { ItemType } from "@knowt/syncing/graphql/schema";
import {
    FLASHCARD_FORMAT_LABEL_MAP,
    FlashcardFormatEnum,
    NOTE_FORMAT_LABEL_MAP,
    NoteFormatEnum,
} from "@knowt/syncing/hooks/ai/constants";
import { FLASHCARD_LANGUAGES } from "@knowt/syncing/hooks/flashcards/languageUtils";
import { useFlashcardSet } from "@knowt/syncing/hooks/flashcards/useFlashcards";
import { useNote } from "@knowt/syncing/hooks/notes/useNotes";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { updateUserInfo } from "@knowt/syncing/hooks/user/utils";
import useCombinedState from "@knowt/syncing/utils/hooks/useCombinedState";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import { omit } from "lodash-es";
import { ArrowDownIcon, ArrowRightIcon, ChevronDown } from "lucide-react";
import { useCallback, useMemo } from "react";
import toast from "react-hot-toast";

type CopilotSettingsPopupProps = {
    isOpen: boolean;
    onClose: () => void;
} & (
    | {
          flashcardSetId: string;
          noteId?: never;
      }
    | {
          flashcardSetId?: never;
          noteId: string;
      }
);

// TODO: move to backend
enum LengthEnum {
    SHORT = "Short",
    MEDIUM = "Medium",
    LONG = "Long",
}

enum SpeedEnum {
    SLOW = "Slow",
    AVERAGE = "Average",
    QUICK = "Quick",
}

const LengthToValueMap: Record<LengthEnum, number> = {
    [LengthEnum.SHORT]: 1,
    [LengthEnum.MEDIUM]: 2,
    [LengthEnum.LONG]: 3,
};

const SpeedToSeconds: Record<SpeedEnum, number> = {
    [SpeedEnum.SLOW]: 4000,
    [SpeedEnum.AVERAGE]: 2000,
    [SpeedEnum.QUICK]: 1000,
};

const SecondsToSliderValue = {
    1000: 3,
    2000: 2,
    4000: 1,
};

const StyledSlider = ({
    onChange,
    value,
}: {
    onChange: (event: Event, value: number | number[]) => void;
    value: number;
}) => (
    <Slider
        value={value}
        onChange={onChange}
        shiftStep={1}
        step={1}
        marks
        min={1}
        max={3}
        sx={{
            maxWidth: "90%",
            margin: "auto",
            height: "0.7rem",
            color: themeColors.neutralBlack,
            "& .MuiSlider-thumb": {
                "&:hover": {
                    boxShadow: "0 0 0 8px rgba(0, 0, 0, 0.16)",
                },
                "&:focus": {
                    boxShadow: "0 0 0 8px rgba(0, 0, 0, 0.16)",
                },
                "&:active": {
                    boxShadow: "0 0 0 8px rgba(0, 0, 0, 0.16)",
                },
            },
            "& .MuiSlider-thumb.Mui-active": {
                boxShadow: "0 0 0 8px rgba(0, 0, 0, 0.16)",
            },
        }}
    />
);

const CopilotSettingsPopup = ({ isOpen, onClose, flashcardSetId, noteId }: CopilotSettingsPopupProps) => {
    const { user } = useCurrentUser();

    const { updateFlashcardSetMetaData, commitUpdates, flashcardSet } = useFlashcardSet({
        flashcardSetId,
        isEnabled: !!flashcardSetId,
    });

    const { update: updateNote, note } = useNote({
        noteId,
        isEnabled: !!noteId,
    });

    const defaultSettings = useMemo(() => {
        return {
            language: user?.autocompleteSettings?.language || FLASHCARD_LANGUAGES.ENGLISH,
            grade: user?.autocompleteSettings?.grade || "9th",
            style: user?.autocompleteSettings?.style || FlashcardFormatEnum.VOCABULARY,
            format: user?.autocompleteSettings?.format || NoteFormatEnum.BULLET_POINTS,
            disabled: !!user?.autocompleteSettings?.disabled,
            btnDisabled: !!user?.autocompleteSettings?.btnDisabled,
            speed: user?.autocompleteSettings?.speed || SpeedToSeconds[SpeedEnum.QUICK],
            length: user?.autocompleteSettings?.length || LengthEnum.MEDIUM,
        };
    }, [user]);

    const itemSettings = useMemo(() => {
        return noteId ? note?.autocompleteSettings : flashcardSet?.autocompleteSettings;
    }, [noteId, note?.autocompleteSettings, flashcardSet?.autocompleteSettings]);

    const [states, updateStates] = useCombinedState(itemSettings || defaultSettings);

    const currentSettingsSameAsDefaults = useMemo(
        () =>
            Object.keys(states).every(key => {
                return states[key] === defaultSettings[key];
            }),
        [defaultSettings, states]
    );

    const hasModified = useMemo(() => {
        return itemSettings
            ? Object.keys(states).some(key => {
                  return states[key] !== itemSettings[key];
              })
            : !currentSettingsSameAsDefaults;
    }, [states, itemSettings, currentSettingsSameAsDefaults]);

    const handleSaveAndClose = useCallback(async () => {
        if (!hasModified) return onClose();

        if (!note && !flashcardSet) {
            toast.error("Failed, try again later", { id: "saving" });
            return onClose();
        }

        toast.loading("Saving...", { id: "saving" });

        try {
            if (noteId) {
                await updateNote({
                    autocompleteSettings: states,
                });
            } else {
                await updateFlashcardSetMetaData({
                    autocompleteSettings: states,
                });
                await commitUpdates();
            }

            if (
                states.btnDisabled !== itemSettings?.btnDisabled ||
                states.disabled !== itemSettings?.disabled ||
                states.speed !== itemSettings?.speed
            ) {
                toast.success(
                    "Your settings have been saved. Please reload your page for the changes to take effect.",
                    {
                        id: "saving",
                    }
                );
            } else {
                toast.success("Your settings have been saved.", { id: "saving" });
            }
            onClose();
            await Mixpanel.track("Kai Complete Settings - Changed", {
                oldSettings: itemSettings || defaultSettings,
                newSettings: states,
                ...(noteId ? { noteId } : { flashcardSetId }),
                itemType: noteId ? ItemType.NOTE : ItemType.FLASHCARDSET,
            });
        } catch {
            toast.error("Failed, try again later", { id: "saving" });
            onClose();
        }
    }, [
        hasModified,
        onClose,
        note,
        flashcardSet,
        noteId,
        states,
        itemSettings,
        defaultSettings,
        flashcardSetId,
        updateNote,
        updateFlashcardSetMetaData,
        commitUpdates,
    ]);

    const renderLanguageInput = () => (
        <FlexRowAlignCenter style={{ justifyContent: "space-between", marginBottom: "2.5rem" }}>
            <p className="bodyBold1">Language</p>
            <LanguageAutocomplete
                initialLanguage={states.language}
                onChange={({ language }) => {
                    updateStates({ language });
                }}
                sx={{ width: "22rem" }}
            />
        </FlexRowAlignCenter>
    );

    const renderGradeInput = () => (
        <FlexRowAlignCenter style={{ justifyContent: "space-between", marginBottom: "2.5rem" }}>
            <p className="bodyBold1">Grade</p>
            <Select
                menuSx={{ width: "25rem", padding: "1rem" }}
                btnSx={{
                    width: "fit-content",
                    gap: "1rem",
                    height: "4.5rem",
                    borderRadius: "999px",
                    backgroundColor: themeColors.neutralWhite,
                    border: `1px solid ${themeColors.neutral3}`,
                }}
                transitionOrigin={{
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                }}
                adornment={<ChevronDown size={iconSizes.SM} fill={themeColors.neutralBlack} />}
                options={new Array(12).fill(0).map((_, i) => ({
                    value: `${i + 1}th`,
                    label: `${i + 1}th`,
                }))}
                selected={{
                    value: states.grade,
                    label: states.grade,
                }}
                onChange={({ value }) => updateStates({ grade: value })}
            />
        </FlexRowAlignCenter>
    );

    const renderLengthInput = () => (
        <FlexColumn
            as={Box}
            style={{
                gap: spacing.SM,
                marginBottom: "2.5rem",
                width: "100%",
                maxWidth: "100%",
            }}>
            <p className="bodyBold1">How long should suggestions be?</p>
            <StyledSlider
                value={LengthToValueMap[states.length as LengthEnum]}
                onChange={(_, value) => {
                    updateStates({
                        length: Object.keys(LengthToValueMap).find(key => LengthToValueMap[key] === value),
                    });
                }}
            />
            <FlexRowAlignCenter style={{ justifyContent: "space-between", width: "100%" }}>
                {Object.values(LengthEnum).map(length => (
                    <p className="bodyBold2" key={length}>
                        {length}
                    </p>
                ))}
            </FlexRowAlignCenter>
        </FlexColumn>
    );

    const renderSpeedInput = () => (
        <FlexColumn
            as={Box}
            style={{
                gap: spacing.SM,
                marginBottom: "2.5rem",
                width: "100%",
                maxWidth: "100%",
            }}>
            <p className="bodyBold1">How quick should suggestions be?</p>
            <StyledSlider
                value={SecondsToSliderValue[states.speed]}
                onChange={(_, value) => {
                    updateStates({
                        speed: Number(
                            Object.keys(SecondsToSliderValue).find(key => SecondsToSliderValue[key] === value)
                        ),
                    });
                }}
            />
            <FlexRowAlignCenter style={{ justifyContent: "space-between", width: "100%" }}>
                {Object.values(SpeedEnum).map(speed => (
                    <p className="bodyBold2" key={speed}>
                        {speed}
                    </p>
                ))}
            </FlexRowAlignCenter>
        </FlexColumn>
    );

    const renderItemFormatInput = () => (
        <FlexColumn
            style={{
                gap: spacing.XS,
                marginBottom: "2.5rem",
            }}>
            <p className="bodyBold1">{noteId ? "What format is your note?" : "What style are your flashcards?"}</p>
            <FlexRowAlignCenter
                style={{
                    gap: spacing.XS_2,
                    width: "100%",
                    flexWrap: "wrap",
                }}>
                {Object.values(
                    noteId ? omit(NOTE_FORMAT_LABEL_MAP, NoteFormatEnum.PAGE_BY_PAGE) : FLASHCARD_FORMAT_LABEL_MAP
                ).map((option: { label: string; value: NoteFormatEnum | FlashcardFormatEnum }) => (
                    <FlexRowAlignCenter
                        className={br.smDownWidth100}
                        onClick={() =>
                            updateStates({
                                style: noteId ? undefined : (option.value as FlashcardFormatEnum),
                                format: noteId ? (option.value as NoteFormatEnum) : undefined,
                            })
                        }
                        key={option.value}
                        style={{
                            border: `1px solid ${
                                option.value === (noteId ? states.format : states.style)
                                    ? themeColors.neutral3
                                    : themeColors.neutral1
                            }`,
                            backgroundColor:
                                option.value === (noteId ? states.format : states.style)
                                    ? themeColors.background
                                    : themeColors.neutralWhite,
                            borderRadius: "1rem",
                            padding: `${spacing.XS} ${spacing.XL}`,
                            minWidth: "25rem",
                            justifyContent: "center",
                            cursor: "pointer",
                        }}>
                        <p className="body2">{option.label}</p>
                    </FlexRowAlignCenter>
                ))}
            </FlexRowAlignCenter>
        </FlexColumn>
    );

    const renderDisableSettings = () => (
        <FlexColumn
            style={{
                gap: spacing.SM,
                marginBottom: "2.5rem",
            }}>
            <p className="bodyBold1">General</p>
            <FlexRowAlignCenter style={{ justifyContent: "space-between" }}>
                <p className="body2">Kai Complete Suggestions</p>
                <ToggleSwitch
                    checked={!states.disabled}
                    onChange={() => updateStates({ disabled: !states.disabled })}
                />
            </FlexRowAlignCenter>
            <FlexRowAlignCenter style={{ justifyContent: "space-between" }}>
                <p className="body2">Kai Complete Buttons</p>
                <ToggleSwitch
                    checked={!states.btnDisabled}
                    onChange={() => updateStates({ btnDisabled: !states.btnDisabled })}
                />
            </FlexRowAlignCenter>
        </FlexColumn>
    );

    const renderKeyboardShortcut = () => (
        <FlexColumn
            style={{
                gap: spacing.SM,
                marginBottom: "2.5rem",
            }}>
            <p className="bodyBold1">Shortcut</p>
            <FlexRowAlignCenter
                style={{
                    width: "100%",
                    justifyContent: "space-between",
                }}>
                <FlexColumn
                    style={{
                        maxWidth: "90%",
                    }}>
                    <p className="bodyBold2">Accept a suggestion</p>
                    <p className="secondaryText1">Use the right arrow key or tab key on your keyboard to accept</p>
                </FlexColumn>
                <FlexRowAlignCenter
                    style={{
                        padding: spacing.XS_2,
                        border: `1px solid ${themeColors.neutral1}`,
                        borderRadius: "1rem",
                    }}>
                    <ArrowRightIcon size={iconSizes.SM} strokeWidth={2} />
                </FlexRowAlignCenter>
            </FlexRowAlignCenter>
            <FlexRowAlignCenter
                style={{
                    width: "100%",
                    justifyContent: "space-between",
                }}>
                <FlexColumn
                    style={{
                        maxWidth: "90%",
                    }}>
                    <p className="bodyBold2">Regenerate a suggestion</p>
                    <p className="secondaryText1">Use the shift and down arrow key on your keyboard to accept</p>
                </FlexColumn>
                <FlexRowAlignCenter
                    style={{
                        gap: spacing.XS,
                    }}>
                    <FlexRowAlignCenter
                        style={{
                            padding: spacing.XS_2,
                            border: `1px solid ${themeColors.neutral1}`,
                            borderRadius: "1rem",
                        }}>
                        shift
                    </FlexRowAlignCenter>
                    <span className="secondaryTextBold2">+</span>
                    <FlexRowAlignCenter
                        style={{
                            padding: spacing.XS_2,
                            border: `1px solid ${themeColors.neutral1}`,
                            borderRadius: "1rem",
                        }}>
                        <ArrowDownIcon size={iconSizes.SM} strokeWidth={2} />
                    </FlexRowAlignCenter>
                </FlexRowAlignCenter>
            </FlexRowAlignCenter>
            <FlexRowAlignCenter
                style={{
                    width: "100%",
                    justifyContent: "space-between",
                }}>
                <FlexColumn
                    style={{
                        maxWidth: "90%",
                    }}>
                    <p className="bodyBold2">Trigger a suggestion</p>
                    <p className="secondaryText1">Use the shift and down arrow key on your keyboard to trigger</p>
                </FlexColumn>
                <FlexRowAlignCenter
                    style={{
                        gap: spacing.XS,
                    }}>
                    <FlexRowAlignCenter
                        style={{
                            padding: spacing.XS_2,
                            border: `1px solid ${themeColors.neutral1}`,
                            borderRadius: "1rem",
                        }}>
                        shift
                    </FlexRowAlignCenter>
                    <span className="secondaryTextBold2">+</span>
                    <FlexRowAlignCenter
                        style={{
                            padding: spacing.XS_2,
                            border: `1px solid ${themeColors.neutral1}`,
                            borderRadius: "1rem",
                        }}>
                        <ArrowDownIcon size={iconSizes.SM} strokeWidth={2} />
                    </FlexRowAlignCenter>
                </FlexRowAlignCenter>
            </FlexRowAlignCenter>
        </FlexColumn>
    );

    return (
        <ResponsiveDialog
            isOpen={isOpen}
            onClose={handleSaveAndClose}
            DialogStyle={{
                borderRadius: borderRadius.circular,
                position: "relative",
                padding: "4rem",
                width: "70.6rem",
                backgroundColor: themeColors.neutralWhite,
            }}
            SlideMenuStyle={{ padding: "3rem 2rem" }}>
            <FlexRowAlignCenter style={{ width: "100%", justifyContent: "space-between", marginBottom: "1.2rem" }}>
                <p className="heading4">Kai Complete Settings</p>
                <DialogCloseBtn onClick={handleSaveAndClose} />
            </FlexRowAlignCenter>

            <p className="body1" style={{ marginBottom: "2.5rem" }}>
                Adjust how autocomplete works for this document.
            </p>

            {renderKeyboardShortcut()}
            {renderDisableSettings()}
            {renderLanguageInput()}
            {renderGradeInput()}
            {renderLengthInput()}
            {renderSpeedInput()}
            {renderItemFormatInput()}

            {!currentSettingsSameAsDefaults && (
                <FlexRowAlignCenter
                    style={{ width: "100%", justifyContent: "flex-end", gap: "1.6rem", marginTop: "2rem" }}>
                    <CircularRectTextButton
                        component="div"
                        className="bodyBold2"
                        onClick={() => updateStates(defaultSettings)}
                        sx={{
                            background: themeColors.neutralWhite,
                            color: themeColors.neutralBlack,
                            height: "4.2rem",
                            width: "auto",
                            padding: "0 1.6rem",
                            border: `1px solid ${themeColors.neutral1}`,
                        }}>
                        <span className="bodyBold2" style={{ color: themeColors.neutralBlack }}>
                            Restore defaults
                        </span>
                    </CircularRectTextButton>

                    <CircularRectTextButton
                        component="div"
                        className="bodyBold2"
                        onClick={async () => {
                            try {
                                await updateUserInfo({
                                    autocompleteSettings: states,
                                });
                                handleSaveAndClose();
                            } catch {
                                toast.error("Failed, try again later", { id: "saving-as-default" });
                            }
                        }}
                        sx={{
                            background: themeColors.neutralBlack,
                            color: themeColors.neutralWhite,
                            height: "4.2rem",
                            width: "auto",
                            padding: "0 1.6rem",
                            border: "none",
                        }}>
                        <span className="bodyBold2" style={{ color: themeColors.neutralWhite }}>
                            Save settings
                        </span>
                    </CircularRectTextButton>
                </FlexRowAlignCenter>
            )}
        </ResponsiveDialog>
    );
};

export default CopilotSettingsPopup;
